"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { startOfDayISO } from "@/lib/utils/date";
import Link from "next/link";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";
import { Button } from "@/components/ui/button";
import { RichTextField } from "@/components/typography/RichTextField";
import { ExpertPreviewModal, type ExpertPreviewData } from "@/components/landing/ExpertPreviewModal";
import { useIsLandingPage } from "@/lib/landing/LandingContext";
import { trackEvent } from "@/lib/analytics";
import ExpertMiniCard from "./ExpertMiniCard";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import type { AssetStoryblok, MultilinkStoryblok } from "@/component-types-sb";
import {
  fetchStoryblokList,
  fetchEventsAndTravels,
} from "@/lib/storyblok/fetchList";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

// Background color options for the gallery
type GalleryBackgroundColor = "none" | "white" | "sand-100" | "sky-50";

const backgroundColorClasses: Record<GalleryBackgroundColor, string> = {
  none: "bg-transparent",
  white: "bg-white",
  "sand-100": "bg-sand-100",
  "sky-50": "bg-sky-50",
};

// Text colors based on background
const textColorClasses: Record<GalleryBackgroundColor, string> = {
  none: "text-black",
  white: "text-black",
  "sand-100": "text-black",
  "sky-50": "text-black",
};

type ExpertStoryblok = {
  name: string;
  title?: string;
  text?: string;
  image?: AssetStoryblok;
  instagram_link?: MultilinkStoryblok;
  linkedin_link?: MultilinkStoryblok;
  _uid: string;
  component: "expert";
  [k: string]: any;
};

type ResolvedExpert = ExpertStoryblok & { href?: string };

function resolveExpert(
  item:
    | string
    | ExpertStoryblok
    | { content: ExpertStoryblok; full_slug?: string; [k: string]: any },
): ResolvedExpert | null {
  // Unresolved reference — just a UUID string
  if (typeof item === "string") return null;
  if ("content" in item && item.content?.component === "expert") {
    return {
      ...item.content,
      href: item.full_slug ? `/${item.full_slug}` : undefined,
    };
  }
  if (item.component === "expert") {
    return item as ExpertStoryblok;
  }
  return null;
}

type ButtonType =
  | ""
  | "none"
  | "link"
  | "next_diatalk"
  | "next_reisen"
  | "next_topictalk"
  | "next_sprechstunde"
  | "next_community";

const buttonTypeToCategory: Record<string, string> = {
  next_diatalk: "diaTalk",
  next_reisen: "reisen",
  next_topictalk: "topicTalk",
  next_sprechstunde: "sprechstunde",
  next_community: "community",
};

type ExpertMiniGalleryStoryblok = {
  title?: string;
  text?: string;
  experts?: (
    | ExpertStoryblok
    | { content: ExpertStoryblok; [k: string]: any }
  )[];
  background_color?: GalleryBackgroundColor;
  button_label?: string;
  button_type?: ButtonType;
  button_link?: MultilinkStoryblok;
  _uid: string;
  component: "expert_mini_gallery";
  [k: string]: any;
};

type ExpertMiniGalleryProps = {
  blok: ExpertMiniGalleryStoryblok;
};

const ExpertMiniGallery = ({ blok }: ExpertMiniGalleryProps) => {
  const isLanding = useIsLandingPage();
  const [previewExpert, setPreviewExpert] = useState<ExpertPreviewData | null>(null);
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const buttonType = blok.button_type || "none";
  const eventCategory = buttonTypeToCategory[buttonType];
  const [nextEventHref, setNextEventHref] = useState<string | null>(null);
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const experts = (blok.experts || [])
    .map(resolveExpert)
    .filter((e): e is ResolvedExpert => e !== null);

  const expertCount = experts.length;
  const bgColor = blok.background_color || "white";
  const bgClass = backgroundColorClasses[bgColor];
  const textClass = textColorClasses[bgColor];

  // Check if carousel can scroll (i.e., content overflows)
  const checkCanScroll = useCallback(() => {
    if (!api) return;
    const canScrollNext = api.canScrollNext();
    const canScrollPrev = api.canScrollPrev();
    setCanScroll(canScrollNext || canScrollPrev);
  }, [api]);

  useEffect(() => {
    if (!api) return;

    // Initial check
    checkCanScroll();

    // Re-check on resize
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);

    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  // Track viewport visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Control autoplay based on canScroll and viewport visibility
  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;

    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  // Fetch next event for dynamic button types
  useEffect(() => {
    if (!eventCategory) return;

    const fetchNextEvent = async () => {
      const now = startOfDayISO();

      try {
        // Trips are their own content type without a category; the
        // info sessions about them are still events with category
        // `reiseInfo`. Both belong under "Reisen" for a visitor.
        const stories =
          eventCategory === "reisen"
            ? await fetchEventsAndTravels({
                filter_query: { date: { gt_date: now } },
                eventOnlyFilter: { category: { in: "reiseInfo" } },
                sort_by: "content.date:asc",
                per_page: 1,
                exact: true,
              })
            : await fetchStoryblokList({
                content_type: "event",
                filter_query: {
                  date: { gt_date: now },
                  category: { in: eventCategory },
                },
                sort_by: "content.date:asc",
                per_page: 1,
              });

        if (stories && stories.length > 0) {
          setNextEventHref(`/${stories[0].full_slug}`);
        }
      } catch (error) {
        console.error("Error fetching next event:", error);
      }
    };

    fetchNextEvent();
  }, [eventCategory]);

  const resolvedHref = eventCategory
    ? nextEventHref
    : getUrlFromLinkObject(blok.button_link) || null;
  const buttonHref = isLanding ? null : resolvedHref;
  const hasButton = blok.button_label && buttonHref;

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full max-w-7xl mx-auto">
      <section
        className={`${bgClass} py-8 md:py-12 flex flex-col gap-4 items-center w-full rounded-3xl md:rounded-none`}
      >
        {/* Header */}
        <div
          className={`flex flex-col gap-4 items-center text-center ${textClass} px-6 md:w-8/12 mb-2`}
        >
          {blok.title && <h2 className="text-headline-xl">{blok.title}</h2>}
          {blok.text && <RichTextField value={blok.text} className="text-copy" />}
          {hasButton && (
            <Button variant="black" size="sm" asChild>
              <Link
                href={buttonHref!}
                target={blok.button_link?.target || "_self"}
              >
                {blok.button_label}
              </Link>
            </Button>
          )}
        </div>

        {/* Expert Mini Cards Carousel */}
        {expertCount > 0 && (
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: canScroll,
              dragFree: false,
              watchResize: true,
              breakpoints: {
                "(min-width: 768px)": {
                  align: "start",
                  dragFree: true,
                },
              },
            }}
            plugins={[autoplayRef.current]}
            className="w-full relative"
          >
            <GalleryNavPrevious show={canScroll} />
            <CarouselContent className={canScroll ? "ml-0" : "justify-center"}>
              {experts.map((expert) => (
                <CarouselItem
                  key={expert._uid}
                  className={
                    canScroll
                      ? "pl-4 basis-auto card-rotate-container"
                      : "basis-auto px-2 card-rotate-container"
                  }
                >
                  <ExpertMiniCard
                    name={expert.name}
                    title={expert.title}
                    image={expert.image}
                    href={isLanding ? undefined : expert.href}
                    onPreviewClick={isLanding ? () => {
                      setPreviewExpert({
                        name: expert.name,
                        title: expert.title,
                        text: expert.text,
                        image: expert.image,
                      });
                      trackEvent("content_preview_opened", {
                        type: "expert",
                        title: expert.name,
                        source: "expert_mini_gallery",
                      });
                    } : undefined}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <GalleryNavNext show={canScroll} />
          </Carousel>
        )}
      </section>
      {previewExpert && (
        <ExpertPreviewModal expert={previewExpert} onClose={() => setPreviewExpert(null)} />
      )}
    </div>
  );
};

export default ExpertMiniGallery;
